import { useEffect, useState } from "react";
import { useSettings } from "@/lib/settings";

/**
 * Injects analytics + custom scripts on public site.
 * Reads from settings.analytics.
 */
export default function AnalyticsInjector() {
  const { settings } = useSettings();
  const [injected, setInjected] = useState({});

  useEffect(() => {
    if (!settings?.analytics) return;
    const a = settings.analytics;

    // GA4
    if (a.ga4_id && !injected.ga4) {
      const s1 = document.createElement("script");
      s1.async = true;
      s1.src = `https://www.googletagmanager.com/gtag/js?id=${a.ga4_id}`;
      document.head.appendChild(s1);
      const s2 = document.createElement("script");
      s2.text = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${a.ga4_id}');`;
      document.head.appendChild(s2);
      setInjected((p) => ({ ...p, ga4: true }));
    }

    // GTM
    if (a.gtm_id && !injected.gtm) {
      const s = document.createElement("script");
      s.text = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${a.gtm_id}');`;
      document.head.appendChild(s);
      setInjected((p) => ({ ...p, gtm: true }));
    }

    // Meta Pixel
    if (a.meta_pixel_id && !injected.mp) {
      const s = document.createElement("script");
      s.text = `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init','${a.meta_pixel_id}');fbq('track','PageView');`;
      document.head.appendChild(s);
      setInjected((p) => ({ ...p, mp: true }));
    }

    // Microsoft Clarity
    if (a.clarity_id && !injected.clarity) {
      const s = document.createElement("script");
      s.text = `(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);})(window,document,'clarity','script','${a.clarity_id}');`;
      document.head.appendChild(s);
      setInjected((p) => ({ ...p, clarity: true }));
    }

    // Hotjar
    if (a.hotjar_id && !injected.hotjar) {
      const s = document.createElement("script");
      s.text = `(function(h,o,t,j,a,r){h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};h._hjSettings={hjid:${a.hotjar_id},hjsv:6};a=o.getElementsByTagName('head')[0];r=o.createElement('script');r.async=1;r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;a.appendChild(r);})(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;
      document.head.appendChild(s);
      setInjected((p) => ({ ...p, hotjar: true }));
    }

    // Search Console verification meta
    if (a.search_console_verification && !injected.sc) {
      const m = document.createElement("meta");
      m.name = "google-site-verification";
      m.content = a.search_console_verification;
      document.head.appendChild(m);
      setInjected((p) => ({ ...p, sc: true }));
    }

    // Custom head scripts (raw HTML) — sanitized via DOMParser to prevent XSS from unexpected content
    if (a.custom_head_scripts && !injected.head) {
      try {
        const parsed = new DOMParser().parseFromString(`<div>${a.custom_head_scripts}</div>`, "text/html");
        const scripts = parsed.querySelectorAll("script");
        scripts.forEach((old) => {
          const n = document.createElement("script");
          // Only copy known-safe attributes
          const allowed = ["src", "async", "defer", "type", "crossorigin", "integrity", "nonce"];
          [...old.attributes].forEach((at) => {
            if (allowed.includes(at.name.toLowerCase())) n.setAttribute(at.name, at.value);
          });
          if (old.textContent) n.text = old.textContent;
          document.head.appendChild(n);
        });
      } catch (e) {
        console.error("Custom head script injection failed:", e);
      }
      setInjected((p) => ({ ...p, head: true }));
    }
  }, [settings, injected]);

  return null;
}
